/**
 * Touch Panel Firestore service (Phase 3).
 * Collection: tenants/{tenantId}/touchPanels/{panelId}
 * Documents also store tenantId for schema compatibility / isolation checks.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  createDefaultPanel,
  createLocalId,
  PANEL_SCHEMA_VERSION,
  DEFAULT_PANEL_BG,
} from '../pages/Admin/TouchPanelDesigner/panelModel';

export const TOUCH_PANELS_COLLECTION = 'touchPanels';

function ensureTenantId(tenantId) {
  if (!tenantId || !String(tenantId).trim()) {
    throw new Error('tenantId is required');
  }
  return String(tenantId).trim();
}

function nowIso() {
  return new Date().toISOString();
}

function panelsCollection(tenantId) {
  return collection(db, 'tenants', ensureTenantId(tenantId), TOUCH_PANELS_COLLECTION);
}

function panelDocRef(tenantId, panelId) {
  if (!panelId) throw new Error('panelId is required');
  return doc(db, 'tenants', ensureTenantId(tenantId), TOUCH_PANELS_COLLECTION, panelId);
}

/**
 * Normalize a Firestore doc into a panel document.
 */
export function mapPanelDoc(snapshot) {
  if (!snapshot?.exists()) return null;
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    tenantId: data.tenantId || null,
    name: data.name || 'Untitled Panel',
    description: data.description || '',
    schemaVersion: data.schemaVersion ?? PANEL_SCHEMA_VERSION,
    createdAt: data.createdAt || null,
    createdBy: data.createdBy || null,
    updatedAt: data.updatedAt || null,
    updatedBy: data.updatedBy || null,
    revision: Number(data.revision) > 0 ? Number(data.revision) : 1,
    branding: {
      logoUrl: data.branding?.logoUrl ?? null,
    },
    appearance: {
      backgroundColor: data.appearance?.backgroundColor || DEFAULT_PANEL_BG,
    },
    defaultScreenId: data.defaultScreenId || null,
    screens: Array.isArray(data.screens) ? data.screens : [],
  };
}

/**
 * Editable content fingerprint (excludes audit/revision fields).
 * Used for dirty tracking.
 */
export function getPanelContentFingerprint(panel) {
  if (!panel) return '';
  const content = {
    name: panel.name || '',
    description: panel.description || '',
    schemaVersion: panel.schemaVersion ?? PANEL_SCHEMA_VERSION,
    branding: { logoUrl: panel.branding?.logoUrl ?? null },
    appearance: {
      backgroundColor: panel.appearance?.backgroundColor || DEFAULT_PANEL_BG,
    },
    defaultScreenId: panel.defaultScreenId || null,
    screens: panel.screens || [],
  };
  return JSON.stringify(content);
}

/**
 * Strip / prepare a panel payload for Firestore write (no undefined).
 */
export function buildPanelWritePayload(panel, { tenantId, userId, revision, isCreate = false } = {}) {
  const tid = ensureTenantId(tenantId);
  const iso = nowIso();
  const payload = {
    id: panel.id,
    tenantId: tid,
    name: (panel.name || '').trim() || 'Untitled Panel',
    description: panel.description || '',
    schemaVersion: panel.schemaVersion ?? PANEL_SCHEMA_VERSION,
    revision: Number(revision) > 0 ? Number(revision) : 1,
    branding: {
      logoUrl: panel.branding?.logoUrl ?? null,
    },
    appearance: {
      backgroundColor: panel.appearance?.backgroundColor || DEFAULT_PANEL_BG,
    },
    defaultScreenId: panel.defaultScreenId || null,
    screens: Array.isArray(panel.screens) ? panel.screens : [],
    updatedAt: iso,
    updatedBy: userId || null,
  };

  if (isCreate) {
    payload.createdAt = panel.createdAt || iso;
    payload.createdBy = panel.createdBy || userId || null;
  } else {
    payload.createdAt = panel.createdAt || iso;
    payload.createdBy = panel.createdBy || userId || null;
  }

  return payload;
}

/**
 * List panels for a tenant, newest updated first.
 */
export async function listTouchPanels(tenantId) {
  const snap = await getDocs(query(panelsCollection(tenantId), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => mapPanelDoc(d)).filter(Boolean);
}

/**
 * Load one panel. Returns null if missing.
 */
export async function getTouchPanel(tenantId, panelId) {
  const snap = await getDoc(panelDocRef(tenantId, panelId));
  return mapPanelDoc(snap);
}

/**
 * Create a new Office Panel (Home + Paging) for the tenant.
 */
export async function createTouchPanel(tenantId, userId, overrides = {}) {
  const tid = ensureTenantId(tenantId);
  const seed = createDefaultPanel();
  const panelId = overrides.id || createLocalId('panel');
  const draft = {
    ...seed,
    ...overrides,
    id: panelId,
    name: overrides.name || seed.name,
    description: overrides.description ?? seed.description,
    tenantId: tid,
  };

  const payload = buildPanelWritePayload(draft, {
    tenantId: tid,
    userId,
    revision: 1,
    isCreate: true,
  });

  await setDoc(panelDocRef(tid, panelId), payload);
  return { ...payload };
}

/**
 * Save panel edits. Increments revision.
 */
export async function saveTouchPanel(tenantId, panel, userId) {
  const tid = ensureTenantId(tenantId);
  if (!panel?.id) throw new Error('panel.id is required');

  const existing = await getTouchPanel(tid, panel.id);
  if (!existing) {
    throw new Error('Touch panel not found');
  }
  if (existing.tenantId && existing.tenantId !== tid) {
    throw new Error('Touch panel tenant mismatch');
  }

  const nextRevision = (Number(existing.revision) || 0) + 1;
  const payload = buildPanelWritePayload(
    {
      ...panel,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
    },
    {
      tenantId: tid,
      userId,
      revision: nextRevision,
      isCreate: false,
    }
  );

  await setDoc(panelDocRef(tid, panel.id), payload, { merge: false });
  return { ...payload };
}

/**
 * Duplicate a panel: new id, "(Copy)" name, revision starts at 1.
 */
export async function duplicateTouchPanel(tenantId, panelId, userId) {
  const tid = ensureTenantId(tenantId);
  const source = await getTouchPanel(tid, panelId);
  if (!source) {
    throw new Error('Touch panel not found');
  }

  const newId = createLocalId('panel');
  const baseName = (source.name || 'Untitled Panel').trim();
  const copyName = `${baseName} (Copy)`;

  const payload = buildPanelWritePayload(
    {
      ...source,
      id: newId,
      name: copyName,
    },
    {
      tenantId: tid,
      userId,
      revision: 1,
      isCreate: true,
    }
  );

  await setDoc(panelDocRef(tid, newId), payload);
  return { ...payload };
}

/**
 * Delete a panel document.
 * Blocks deletion when active assignments exist.
 */
export async function deleteTouchPanel(tenantId, panelId) {
  const { listAssignmentsForPanel } = await import('./touchPanelAssignmentService.js');
  const assignments = await listAssignmentsForPanel(tenantId, panelId);
  if (assignments.length > 0) {
    const err = new Error(
      `Cannot delete panel while assigned to ${assignments.length} device(s). Unassign first.`
    );
    err.code = 'PANEL_HAS_ASSIGNMENTS';
    err.assignments = assignments;
    throw err;
  }
  await deleteDoc(panelDocRef(tenantId, panelId));
}

/**
 * Client-side search filter by name / description.
 */
export function filterTouchPanels(panels, searchText) {
  const q = (searchText || '').trim().toLowerCase();
  if (!q) return panels || [];
  return (panels || []).filter((p) => {
    const name = (p.name || '').toLowerCase();
    const description = (p.description || '').toLowerCase();
    return name.includes(q) || description.includes(q);
  });
}

/**
 * Sort by updatedAt descending (ISO or Date-compatible).
 */
export function sortTouchPanelsByUpdatedDesc(panels) {
  return [...(panels || [])].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });
}
